export type SubDomainKey = keyof typeof TEMPLATE_SUBDOMAINS

// We want Intellisense for backend links. In order to do that, we have to have a type. 
// Maintaining a separate type for this massive thing is annoying and unnecessary, so we can use the object as a type.
// However, we can't use this object as a type without giving it a value first.
// So, we store template strings in the place of the subdomains and parse them later according to the environment.

export const TEMPLATE_SUBDOMAINS = {
	QUERY: "QUERY_DOMAIN",
	IAM: "IAM_DOMAIN",
}

export const isDevelopment = window.env.REACT_APP_WORKSPACE_NAME === `DEV`
export const isDemo = window.env.REACT_APP_WORKSPACE_NAME === `DEMO`

export const LINKS = Object.freeze({
	QUERY: TEMPLATE_SUBDOMAINS.QUERY,
	LOGIN: {
		GET_TOKEN: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/auth/login`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: false,
		},
		SEND_RESET_PASSWORD_EMAIL: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/send_reset_password_email`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: false,
		},
		DELETE_USER: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/user/delete_user`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true,
		},
		CHANGE_PASSWORD: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/change_password`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true,
		},
		CHECK_IBM_CREDENTIALS: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/ibm/check_ibm_credentials`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		GET_WORKSPACE_ENV: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/workspace/get_workspace_env`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		GET_WORKSPACE_ENV_DETAILS: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/workspace/get_workspace_env_details`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		SET_WORKSPACE_ENV: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/workspace/set_workspace_env`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		OAUTH_LOGIN:{
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/auth/oauth_login`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: false
		},
		OAUTH_AUTHORIZE: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/auth/oauth_authorize`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true
		}
	},
	DATA: {
		TRIALS: {
			GET_MODALITIES_UNION: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_modalities_union`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_MODALITIES_INTERSECTION: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_modalities_intersection`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
		},
		PROFILING: {
			GET_ASSOCIATED_DATA_SOURCES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_associated_data_sources`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENT_FILE_TYPE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patient_file_type`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENT_FILE_START_END_TIMESTAMPS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patient_file_start_end_timestamps`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_ANNOTATION_GROUPS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/get_annotation_groups`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENTS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patients`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENTS_CLINICAL: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patients_clinical`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_ANNOTATION_GROUP: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/create_annotation_group`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			RENAME_ANNOTATION_GROUP: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/rename_annotation_group`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			DELETE_ANNOTATION_GROUP: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/annotation/delete_annotation_group`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			DELETE_ANNOTATION_GROUPS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/delete_annotation_groups`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			GET_ANNOTATIONS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/get_annotations`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			DELETE_ANNOTATIONS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/delete_annotations`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			ADD_ANNOTATION: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/add_annotation`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			MODIFY_ANNOTATION: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation/modify_annotation`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			GET_ANNOTATION_REVIEWS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation_review/get_annotation_reviews`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			ADD_ANNOTATION_REVIEW: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/annotation_review/add_annotation_review`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_LAYOUTS: {
				LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/layout/get_layouts`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_LAYOUT: {
				LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/layout/create_layout`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			DELETE_LAYOUT: {
				LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/layout/delete_layout`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			MODIFY_LAYOUT: {
				LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/layout/modify_layout`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			RENAME_LAYOUT: {
				LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/layout/modify_layout`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
		GET_PATIENT: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patient`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		GET_PATIENT_ATTRIBUTES: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patient_attributes`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		PERSYST: {
				TREND_PANELS: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/persyst/persyst_api_panels`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				EEG_MONTAGES: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/persyst/all_montage_definitions`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
			},
			GET_PATIENTS_FORM_DIRECTORY: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/form/get_patient_forms`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENT_FORM: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/form/get_patient_form`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
		},
		UPLOAD: {
			GET_PROJECT_USER_SITES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/site/get_project_user_sites`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_USER_PROJECTS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/project/get_user_projects`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PROJECT_PATIENTS_BY_SITE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_project_patients_by_site`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			START_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/start_upload`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			FINISH_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/finish_upload`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/create_upload`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_MONITORING_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/create_monitoring_upload`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			MODIFY_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/modify_upload`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CANCEL_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/cancel_upload`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			DELETE_CANCELED_UPLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/delete_canceled_upload`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			UPDATE_MD5_FORM: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/update_md5_form`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			GET_DIRECTORY_STRUCTURE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_directory_structure`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_EXISTING_DATA_FOR_DATATYPE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_existing_data_for_datatype`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENT_DATA_OBJECTS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patient_data_objects`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_MODALITY_TIME_BY_PATIENT: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_modality_time_by_patient`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CHECK_PATIENT_IDENTIFIER: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/check_patient_identifier`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_UPLOAD_MESSAGES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/get_upload_messages`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PATIENT_UPLOADS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/get_patient_uploads`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CHECK_MD5_HASH: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/check_md5_hash`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CHECK_CANCEL_STATUS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/check_cancel_status`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CHECK_CNS_FILES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/check_cns_files`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			UPDATE_MD5_IV: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/update_md5_iv`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			SEND_UPLOAD_NOTIFICATION_EMAIL: {
				LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/upload/send_upload_notification_email`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			DOWNLOAD_FILE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/download_file`,
				OUTPUT_TYPE: "file",
				REQUIRES_TOKEN: true,
			},
			SAVE_SCREENSHOT_TO_EMR: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/upload/save_screenshot_to_emr`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			}
		},
		EXPORTS: {
			START: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/exports`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			LIST: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/exports`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			STATS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/exports/stats`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
		},
	},
	VISUALIZE: {
		EEG: {
			GET_MONTAGE_MAPPING: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/eeg_montage/get_montage_mapping`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_EEG_MONTAGES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/eeg_montage/get_eeg_montages`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_EEG_MONTAGE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/eeg_montage/create_eeg_montage`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			DELETE_EEG_MONTAGE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/eeg_montage/delete_eeg_montage`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			MODIFY_EEG_MONTAGE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/eeg_montage/modify_eeg_montage`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
		},
		GET_ADMISSION_INFO: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_admission_info`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true
		},
		GET_DATA_OBJECT_BY_ID: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_data_object_by_id`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true
		},
		GET_PATIENT_HARMONIZED_MEDICATIONS: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_patient_harmonized_medications`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true
		},
		GET_EMR_DATA: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_emr_data`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true
		},
		GET_ALL_HARMONIZED_MEDICATIONS: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_all_harmonized_medications`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true
		},
		PERSYST: {
			GET_RECORDING_INFO: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/persyst/get_recording_info`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true
			},
			CLEAR_PERSYST_CACHE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/persyst/clear_persyst_cache`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true
			},
		},
		ALERTS: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/alert`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true
		}
	},
	ADMIN: {
		USER_MANAGEMENT: {
			USERS: {
				GET_USERS: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/user/get_workspace_users`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				GET_USERS_BY_PROJECT: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/user/get_workspace_users_by_project`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				GET_USERS_BY_SITE: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/user/get_workspace_users_by_site`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				DELETE_USERS: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/user/delete_users`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				INVITE_USERS: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/user/invite_users`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				MODIFY_USER: {
					LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/user/modify_workspace_user`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				GET_ROLES: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/role/get_roles`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				GET_ROLES_BY_USER: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/role/get_roles_by_user`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				GET_DOMAINS: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/domain/get_domains`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				ADD_DOMAIN: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/domain/add_domain`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				REMOVE_DOMAINS: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/domain/remove_domains`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				SET_RESTRICT_EMAIL_DOMAINS: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/workspace/set_restrict_email_domains`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				GET_DOMAIN_RESTRICTION: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/workspace/get_domain_restriction`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
			},
			ROLES_AND_PERMISSIONS: {
				GET_PERMISSIONS: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/role/get_permissions`,
					OUTPUT_TYPE: "json",
					REQUIRES_TOKEN: true,
				},
				CREATE_ROLE: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/role/create_role`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				DELETE_ROLES: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/role/delete_roles`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
				MODIFY_ROLE: {
					LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/role/modify_role`,
					OUTPUT_TYPE: "text",
					REQUIRES_TOKEN: true,
				},
			},
		},
		PROJECTS: {
			GET_PROJECTS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/project/get_projects`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_PROJECTS_BY_USER: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/project/get_projects_by_user`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_PROJECT: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/project/create_project`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
				ERROR_RESPONSE_TYPE: "json",
			},
			DELETE_PROJECTS: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/project/delete_projects`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			MODIFY_PROJECT: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/project/modify_project`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
		},
		PREFETCH: {
			SNAPSHOT: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/prefetch`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			UNLOAD: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/prefetch/unload`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
		},
		SITES: {
			GET_SITES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/site/get_workspace_sites`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			GET_SITES_BY_USER: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/site/get_workspace_sites_by_user`,
				OUTPUT_TYPE: "json",
				REQUIRES_TOKEN: true,
			},
			CREATE_SITE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/site/create_workspace_site`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
				ERROR_RESPONSE_TYPE: "json",
			},
			DELETE_SITES: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/site/delete_sites`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
			MODIFY_SITE: {
				LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/site/modify_workspace_site`,
				OUTPUT_TYPE: "text",
				REQUIRES_TOKEN: true,
			},
		},
		BUCKETS: {
			GET_BUCKET: {
                LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/bucket/get_bucket`,
                OUTPUT_TYPE: 'json',
                REQUIRES_TOKEN: true
            },
		}
	},
	ACCOUNT: {
		CREATE_ACCOUNT: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/create_account`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true,
		},
		CHANGE_PASSWORD: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/change_password`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true,
		},
		RESET_PASSWORD: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/reset_forgot_password`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: false,
		},
		CHANGE_EMAIL: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/change_email`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true,
		},
		AUTH_SESSION_REFRESH: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/auth/auth_session_refresh`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		LOGOUT: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/auth/logout`,
			OUTPUT_TYPE: "text",
			REQUIRES_TOKEN: true,
		},
		SETUP_INFO: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/account/setup_info`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		CREATE_ANNOTATION_SHORTCUT: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/shortcut/create_annotation_shortcut`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		GET_GENERAL_SHORTCUTS: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/shortcut/get_general_shortcuts`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		GET_ANNOTATION_SHORTCUTS: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/shortcut/get_annotation_shortcuts`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		DELETE_ANNOTATION_SHORTCUTS: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/shortcut/delete_annotation_shortcuts`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		UPDATE_ANNOTATION_SHORTCUT: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/shortcut/update_annotation_shortcut`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
		RESET_ANNOTATION_SHORTCUTS: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/shortcut/reset_annotation_shortcuts`,
			OUTPUT_TYPE: "json",
			REQUIRES_TOKEN: true,
		},
	},
	WORKSPACE: {
		GET_LOGIN_ATTEMPTS: {
			LINK: `${TEMPLATE_SUBDOMAINS.IAM}/v1/workspace/get_login_attempts`,
			OUTPUT_TYPE: 'json',
			REQUIRES_TOKEN: true
		},
	},
    AGGREGATOR: {
        GET_GATEWAYS: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/gateways`,
            OUTPUT_TYPE: 'json',
            REQUIRES_TOKEN: true
        },
        GET_GATEWAY_INFO: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/bed-info`,
            OUTPUT_TYPE: 'json',
            REQUIRES_TOKEN: true
        },
        GET_INTELLIVUE_WAVE_PRIORITY_LIST: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/intellivue/wave-priority-list`,
            OUTPUT_TYPE: 'json',
            REQUIRES_TOKEN: true
        },
        POST_INTELLIVUE_WAVE_PRIORITY_LIST: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/intellivue/wave-priority-list`,
            OUTPUT_TYPE: 'json',
            REQUIRES_TOKEN: true
        },
        POST_ADD_DEVICES: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/device`,
            OUTPUT_TYPE: 'json',
            REQUIRES_TOKEN: true
        },
        START_MONITORING: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/start-monitoring`,
            OUTPUT_TYPE: 'text',
            REQUIRES_TOKEN: true
        },
        TOGGLE_MONITORING: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/toggle-monitoring`,
            OUTPUT_TYPE: 'text',
            REQUIRES_TOKEN: true
        },
        STOP_MONITORING: {
            LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/aggregator/stop-monitoring`,
            OUTPUT_TYPE: 'text',
            REQUIRES_TOKEN: true
        },
    },
	PATIENT: {
		GET_PATIENT_VISIT_NUMBER_RECORDS_FOR_MRN: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/get_visit_number_records_for_mrn`,
			OUTPUT_TYPE: 'json',
			REQUIRES_TOKEN: true
		},
		UPDATE_HARMONIZED_MEDICATION_MAPPING: {
			LINK: `${TEMPLATE_SUBDOMAINS.QUERY}/v1/patient/update_harmonized_medication_mapping`,
			OUTPUT_TYPE: 'text',
			REQUIRES_TOKEN: true
		}
	}
})
